import {
  generateGanttChartCellRendererParams,
  ProjectPlanBulkSheetContext,
  ProjectPlanOptions,
  ProjectPlanRow,
} from '../ProjectPlan/projectPlanOptions'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { APPLICATION_FUNCTION_EXTERNAL_ID } from '../index'
import {
  generateToolBarItemKey,
  ToolBarItemPosition,
} from '../../components/toolbars/ContainerToolBar'
import { OpenInNewIcon } from '../../components/icons/OpenInNewIcon'
import ProjectApi from '../../../lib/functions/project'
import { FunctionLayer } from '../../../store/functionLayer'
import {
  AggregateField,
  WbsItemType,
} from '../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../lib/functions/workload'
import store from '../../../store'
import { GanttParameterVO } from '../../../domain/value-object/GanttParameterVO'
import { taskActualResultExists } from '../../../lib/functions/wbsItem'
import { AgGridValueSetter } from '../../containers/BulkSheet'
import { toNumber } from '../../../utils/number'
import { ValueSetterParams } from 'ag-grid-community'

export default class ProjectPlanTab extends ProjectPlanOptions {
  columnAndFilterStateKey = ctx =>
    `${UiStateKey.ProjectPlanTabColumnAndFilterState}-${ctx.state.uuid}`

  private toLayer = async (props: {
    treeRootUuid: string
    projectUuid: string
  }): Promise<FunctionLayer> => {
    const response = await ProjectApi.getDetail({ uuid: props.projectUuid })
    const project = response.json
    return {
      externalId: APPLICATION_FUNCTION_EXTERNAL_ID.PROJECTPLAN_NEW_EDIT,
      code: project.code,
      params: {
        treeRootUuid: props.treeRootUuid,
      },
    }
  }

  getCellRendererParams = (
    field: string,
    ctx?: ProjectPlanBulkSheetContext
  ): { [key: string]: any } | undefined => {
    if (field === 'wbsItem.displayName') {
      return {
        isRootUuid: (uuid: string) => {
          if (ctx?.props.treeRootUuid === uuid) return true
        },
      }
    }
    if (field === 'wbsItem.actualHour') {
      return {
        link: (row: ProjectPlanRow) =>
          row && taskActualResultExists(row.wbsItem),
      }
    }
    if (field === 'wbsItem.watchers') {
      return {
        showIcon: true,
      }
    }
    if (field === 'ganttChart') {
      if (!ctx) return {}
      return generateGanttChartCellRendererParams(
        ctx,
        ctx.props.ganttParameter ||
          store.getState().project.ganttParameter ||
          GanttParameterVO.defaultValue()
      )
    }
    return undefined
  }

  private superValueSetter = this.getValueSetter
  getValueSetter = (field: string): AgGridValueSetter | undefined => {
    if (
      field !== 'wbsItem.estimatedWorkload.deliverable' &&
      field !== 'wbsItem.estimatedWorkload.task'
    ) {
      return this.superValueSetter(field)
    }
    return (params: ValueSetterParams) => {
      const newValue = toNumber(params.newValue)
      if (params.data.wbsItem.estimatedWorkload) {
        params.data.wbsItem.estimatedWorkload.hour = newValue
        params.api.applyTransaction({ update: [params.data] })
        return true
      }
    }
  }

  headerComponents = ctx => {
    return undefined
  }

  toolBarItems = ctx => [
    <div key={generateToolBarItemKey(1, ToolBarItemPosition.RIGHT)}>
      <OpenInNewIcon size="s" layer={() => this.toLayer(ctx.props)} />
    </div>,
  ]

  hiddenColumns = [
    'wbsItem.estimatedWorkload.deliverable',
    'wbsItem.estimatedWorkload.task',
    'wbsItem.actualHour',
    'progress.progressRate',
    'wbsItem.priority',
    'wbsItem.scheduledDate.startDate',
    'wbsItem.scheduledDate.endDate',
    'wbsItem.actualDate.startDate',
    'wbsItem.actualDate.endDate',
    'wbsItem.team',
    'wbsItem.accountable',
    'wbsItem.responsible',
    'wbsItem.assignee',
    'wbsItem.watchers',
  ]

  getDefaultContext = (): any => ({
    wbsItemType: WbsItemType.TASK,
    aggregateTargetType: AggregateField.WBS_ITEM_WORKLOAD,
    workloadUnit: WorkloadUnit.HOUR,
  })
}
